import React, { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import {
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    FormHelperText,
    ListItemText,
    MenuItem,
    Select,
    Skeleton,
    TextField,
    useTheme,
    SxProps
} from "@mui/material";
import {
    ArrowBackIosFilledRounded,
    ArrowForwardIosFilledRounded,
    CloseRounded,
} from "@mui-symbols-material/w300";
import IconButton from "@mui/joy/IconButton/IconButton";
import { LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import { useTranslation } from "react-i18next";
import { InfrastructureSelect as InfrastructureSelectQuery } from "../../graphql/queries/infrastructureSelect";
import { useInfrastructureNodeList, InfrastructureNode } from "../../hooks/useInfrastructureNodeList";
import { revealRowInScroller } from "../../utils/columnScroll";

const LIST_SCROLL_THRESHOLD = 48;

type PartialInstalledBaseNode = InfrastructureNode;

type Props = {
    onChange?: (id: string) => void;
    value?: string;
    label?: string;
    sx?: SxProps;
    error?: boolean;
    helperText?: string;
    /** Node that must not be picked; shown disabled. Its subtree is blocked
     *  transitively since descendants are only reachable by drilling in. */
    excludeNodeId?: string;
    /** Restricts browsing to the subtree below this node; the company list is
     *  never shown. Read once at mount (same contract as InfrastructureBrowser). */
    rootNodeId?: string;
    /** Only with `rootNodeId`: the scoped root itself is offered for selection
     *  via a checkbox on the top-level navigation entry. */
    rootSelectable?: boolean;
    /** Pass `false` in destination pickers to hide the TRASH node and everything
     *  inside it (elements only reach the trash by being deleted). Omitted, the
     *  server default applies and the trash stays browsable/selectable. */
    includeTrash?: boolean;
};

const InfrastructureSelect = ({ onChange, label, value, sx, error, helperText, excludeNodeId, rootNodeId, rootSelectable, includeTrash }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    // The menu is portaled to <body> and MUI only sets the input width as its
    // minWidth, so long titles (rows never wrap) stretched it far past the
    // input. Pin it to the input's width, measured when it opens.
    const formControlRef = useRef<HTMLDivElement>(null);
    const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

    const normalizedValue = value != null && value !== "" ? String(value) : null;
    const scopedRootId = rootNodeId != null && rootNodeId !== "" ? String(rootNodeId) : null;

    const [isOpen, setIsOpen] = useState(false);
    const [parentNodeId, setParentNodeId] = useState<string | null>(scopedRootId);
    const [parentNodeHistory, setParentNodeHistory] = useState<{ id: string }[]>(
        []
    );

    const isRoot = parentNodeId === null;
    const atTopLevel = scopedRootId !== null ? parentNodeId === scopedRootId : isRoot;

    // Load selected node for display
    const { data: selectedNodeData, loading: selectedNodeLoading } = useQuery(
        InfrastructureSelectQuery,
        {
            variables: { id: normalizedValue },
            skip: !normalizedValue,
            fetchPolicy: "cache-first",
        }
    );

    const selectedNode: PartialInstalledBaseNode | null =
        selectedNodeData?.installedBaseNode ?? null;

    // Keep parentNodeId in sync with value
    useEffect(() => {
        if (selectedNode) {
            const path = selectedNode.pathNodes ?? [];
            const parentOfSelected =
                path.length >= 2 ? path[path.length - 2].id : null;
            setParentNodeId(parentOfSelected ?? scopedRootId);
        } else {
            setParentNodeId(scopedRootId);
        }
    }, [selectedNode?.id]);

    // Load parent node info
    const { data: installedBaseNodeData, loading: installedBaseNodeLoading } =
        useQuery(InfrastructureSelectQuery, {
            variables: { id: parentNodeId },
            skip: !parentNodeId,
            fetchPolicy: "cache-first",
        });

    const {
        searchInput: searchText,
        onSearchInputChange,
        onClearSearch,
        rows: filteredEntries,
        loading: listLoading,
        loadingMore,
        loadMore,
        revealSelectedKey,
    } = useInfrastructureNodeList({
        parentNodeId,
        includeTrash,
        selectedNode,
        normalizedValue,
    });

    const loading = listLoading || installedBaseNodeLoading || selectedNodeLoading;

    // Selection on the first page but below the fold: scroll to it once per
    // opening and level/search/selection. The menu is portaled, so its paper is
    // found via this instance's id.
    const menuId = useId();
    const revealedKeyRef = useRef<string | null>(null);
    // Returning to the selection's level inside the open menu reveals it again.
    useEffect(() => {
        revealedKeyRef.current = null;
    }, [parentNodeId]);
    useEffect(() => {
        if (!isOpen) {
            revealedKeyRef.current = null;
            return;
        }
        if (loading || !revealSelectedKey || revealedKeyRef.current === revealSelectedKey) return;
        revealedKeyRef.current = revealSelectedKey;
        requestAnimationFrame(() => {
            const scroller = document.querySelector<HTMLElement>(`[data-infrastructure-select-menu="${menuId}"]`);
            const row = scroller?.querySelector<HTMLElement>('[data-selected="true"]');
            if (scroller && row) revealRowInScroller(scroller, row);
        });
    }, [isOpen, loading, revealSelectedKey, menuId]);

    let parentNodeText = t("Companies");
    if (typeof parentNodeId === "string") {
        parentNodeText = installedBaseNodeData?.installedBaseNode?.title ?? "";
    }

    const handleMenuScroll = (el: HTMLElement) => {
        if (el.scrollTop + el.clientHeight < el.scrollHeight - LIST_SCROLL_THRESHOLD) return;
        loadMore();
    };

    const handleSelectionChange = (entry: PartialInstalledBaseNode) => {
        const idStr = String(entry.id);

        if (normalizedValue === idStr) {
            // wirklich "kein Wert"
            onChange?.(null as any);
        } else {
            onChange?.(idStr);
        }
        setIsOpen(false);
    };

    const handleParentSelection = (id: string) => {
        setParentNodeHistory((prev) =>
            parentNodeId ? [...prev, { id: parentNodeId }] : prev
        );
        setParentNodeId(id);
    };

    const handlePrevNode = (e: React.MouseEvent) => {
        // The capture-phase stop below would swallow clicks on the root-select
        // checkbox, so let those pass through untouched.
        if ((e.target as HTMLElement).closest?.("[data-root-select]")) return;

        e.stopPropagation();
        e.preventDefault();

        if (atTopLevel) return;

        if (parentNodeHistory.length > 0) {
            const prevParent = parentNodeHistory[parentNodeHistory.length - 1];
            setParentNodeHistory((prev) => prev.slice(0, -1));
            setParentNodeId(prevParent.id);
        } else {
            setParentNodeId(scopedRootId);
        }
    };

    const truncateMiddle = (str: string, maxLength: number) => {
        if (str.length <= maxLength) return str;
        const half = Math.floor((maxLength - 3) / 2);
        return str.slice(0, half) + "..." + str.slice(-half);
    };

    return (
        <FormControl ref={formControlRef} sx={{ width: "100%", ...sx }} size="small" error={error}>
            <Select
                labelId="dropdown-with-search-label"
                id="dropdown-with-search"
                open={isOpen}
                onOpen={() => {
                    setMenuWidth(formControlRef.current?.clientWidth);
                    setIsOpen(true);
                }}
                onClose={() => setIsOpen(false)}
                value={normalizedValue ?? ""}
                label={label}
                renderValue={(selectedId) => {
                    if (!selectedId) return "";
                    if (selectedNodeLoading) return <Skeleton />;

                    if (!selectedNode || selectedNode.id !== selectedId) return "";

                    // Root nodes may come back with an empty/parents-only path — fall back to the title.
                    const fullPath = selectedNode.pathNodes
                        .map((node: any) => node.title)
                        .filter(Boolean)
                        .join(" / ") || (selectedNode.title ?? "");

                    const truncated = truncateMiddle(fullPath, 60);

                    return (
                        <div
                            style={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                maxWidth: "100%",
                            }}
                            title={fullPath}
                        >
                            {truncated}
                        </div>
                    );
                }}
                notched
                MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: {
                        "data-infrastructure-select-menu": menuId,
                        style: {
                            maxHeight: "400px",
                            overflowY: "auto",
                            width: menuWidth,
                            maxWidth: menuWidth,
                        },
                        onScroll: (e: React.UIEvent<HTMLElement>) => handleMenuScroll(e.currentTarget),
                    },
                }}
                sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc",
                        borderRadius: "4px",
                    },
                }}
            >
                {/* Search field */}
                <MenuItem
                    onKeyDown={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder={t("Search")}
                        value={searchText}
                        onChange={(e) => onSearchInputChange(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        size="small"
                        sx={{
                            ".MuiOutlinedInput-root": {
                                "& fieldset": { border: "none !important" },
                                "&:hover fieldset": { border: "none !important" },
                                "&.Mui-focused fieldset": { border: "none !important" },
                            },
                        }}
                        InputProps={{
                            endAdornment:
                                searchText !== "" ? (
                                    <IconButton onClick={onClearSearch}>
                                        <CloseRounded />
                                    </IconButton>
                                ) : null,
                        }}
                    />
                </MenuItem>
                <Divider />

                {/* Navigation + Items */}
                {loading ? (
                    <LoadingScreen />
                ) : (
                    <div>
                        <MenuItem
                            onClickCapture={handlePrevNode}
                            sx={{ color: theme.palette.primary.main }}
                        >
                            {!atTopLevel && <ArrowBackIosFilledRounded style={{ flexShrink: 0 }} />}
                            {rootSelectable && scopedRootId !== null && atTopLevel && (
                                <Checkbox
                                    data-root-select
                                    checked={normalizedValue === scopedRootId}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelectionChange({
                                            id: scopedRootId,
                                            key: "",
                                            pathNodes: [],
                                        });
                                    }}
                                />
                            )}
                            <ListItemText
                                primary={parentNodeText}
                                title={parentNodeText}
                                primaryTypographyProps={{ noWrap: true }}
                                sx={{ minWidth: 0 }}
                            />
                        </MenuItem>
                        {filteredEntries.map((entry) => {
                            const entryId = String(entry.id);
                            const isExcluded = excludeNodeId != null && String(excludeNodeId) === entryId;
                            return (
                                <MenuItem
                                    key={entryId}
                                    value={entryId}
                                    data-selected={normalizedValue === entryId ? "true" : undefined}
                                    disabled={isExcluded}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleParentSelection(entryId);
                                    }}
                                >
                                    <Checkbox
                                        checked={normalizedValue === entryId}
                                        disabled={isExcluded}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectionChange({
                                                id: entryId,
                                                key: entry.key,
                                                pathNodes: entry.pathNodes,
                                            });
                                        }}
                                    />
                                    <ListItemText
                                        primary={entry.title}
                                        title={entry.title ?? undefined}
                                        primaryTypographyProps={{ noWrap: true }}
                                        sx={{ minWidth: 0 }}
                                    />
                                    {!isExcluded && <ArrowForwardIosFilledRounded style={{ flexShrink: 0 }} />}
                                </MenuItem>
                            );
                        })}
                        {loadingMore && (
                            <MenuItem disabled sx={{ justifyContent: "center" }}>
                                <CircularProgress size={18} />
                            </MenuItem>
                        )}
                    </div>
                )}
            </Select>
            <InputLabel id="dropdown-with-search-label" shrink sx={sx}>
                {label}
            </InputLabel>
            {helperText && (
                <FormHelperText>{helperText}</FormHelperText>
            )}
        </FormControl>
    );
};

export default InfrastructureSelect;
